import { useEffect, useMemo, useState } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { format } from 'date-fns'
import { Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { db } from '@/lib/firebase'
import { Input } from '@/components/ui/input'
import { ChatGrid } from '@/components/chat/ChatGrid'
import { BookingsCalendarView } from '@/components/chat/BookingsCalendarView'

/** Matches ChatGrid’s chat shape (admin analytics local format). */
export interface OpsViewChat {
  id: string
  userId: string
  artistId: string
  createdAt: { seconds: number; nanoseconds: number }
  lastMessageAt: { seconds: number; nanoseconds: number } | null
  lastMessage: string | null
  messages: Array<{
    senderType: 'user' | 'artist' | 'assistant'
    content: string
    timestamp: string | { seconds: number; nanoseconds: number }
    isRead: boolean
  }>
  status: string
  artistName?: string
  hasLead?: boolean
  userInstagram?: string | null
  userEmail?: string | null
  offerSentDate?: string | null
  offerAcceptedDate?: string | null
  appointmentDate?: string | null
  bookingPrice?: number | null
  aiBookingDetected?: boolean
  aiBookingDate?: string | null
  aiBookingPrice?: number | null
  platformExitLikelihood?: number
  /** Time4Tats manual slot with no chat thread */
  bookingOnly?: boolean
}

interface BookingAnalysis {
  chatId: string
  isBooked: boolean
  confidence: number
  bookedTime?: string | null
  bookingType?: 'custom' | 'flash' | 'unknown'
  summary?: string
  reasoning?: string
}

export type AnalyticsLoadState = 'idle' | 'filter_refresh' | 'full_refresh'

export interface ArtistCentricOpsViewProps {
  chats: OpsViewChat[]
  statusFilter: string
  sortOrder: 'newest' | 'oldest'
  onDeleteChat: (chatId: string) => Promise<void>
  bookingAnalyses: Map<string, BookingAnalysis>
  analyzingChatIds: Set<string>
  onAnalyzeSingleChat: (chatId: string) => void
  /** Dims and blocks the main grid while analytics reload; full_refresh uses a stronger grey-out. */
  analyticsLoadState?: AnalyticsLoadState
}

function getWaitingStatus(chat: OpsViewChat): 'waiting_user' | 'waiting_artist' | null {
  const lastMessage = chat.messages[chat.messages.length - 1]
  if (lastMessage?.senderType === 'assistant') return 'waiting_artist'
  if (lastMessage?.senderType === 'artist') return 'waiting_user'
  if (lastMessage?.senderType === 'user' && chat.hasLead) return 'waiting_artist'
  return null
}

function getEffectiveBookingDate(
  chat: OpsViewChat,
  bookingAnalyses: Map<string, BookingAnalysis>
): Date | null {
  const analysis = bookingAnalyses.get(chat.id)
  if (analysis?.bookedTime) {
    const d = new Date(analysis.bookedTime)
    if (!Number.isNaN(d.getTime())) return d
  }
  if (chat.appointmentDate) {
    const d = new Date(chat.appointmentDate)
    if (!Number.isNaN(d.getTime())) return d
  }
  if (chat.aiBookingDate) {
    const d = new Date(chat.aiBookingDate)
    if (!Number.isNaN(d.getTime())) return d
  }
  return null
}

function artistInitials(name: string | undefined): string {
  if (!name?.trim()) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

/** Same activity time as ChatGrid (lastMessageAt → last message timestamp → createdAt). */
function getOpsChatActivityMs(chat: OpsViewChat): number {
  if (chat.lastMessageAt?.seconds != null) {
    return chat.lastMessageAt.seconds * 1000
  }
  const last = chat.messages[chat.messages.length - 1]
  if (last?.timestamp != null) {
    if (typeof last.timestamp === 'string') {
      const d = new Date(last.timestamp)
      if (!Number.isNaN(d.getTime())) return d.getTime()
    } else if (typeof last.timestamp === 'object' && 'seconds' in last.timestamp) {
      return last.timestamp.seconds * 1000
    }
  }
  return chat.createdAt.seconds * 1000
}

function bucketLatestActivityMs(bucket: ArtistBucket): number {
  let max = 0
  for (const c of bucket.chats) {
    const t = getOpsChatActivityMs(c)
    if (t > max) max = t
  }
  return max
}

interface ArtistBucket {
  artistId: string
  artistName: string
  chats: OpsViewChat[]
  needsReplyCount: number
  nextBooking: Date | null
}

function buildArtistBuckets(
  chats: OpsViewChat[],
  bookingAnalyses: Map<string, BookingAnalysis>,
  sortOrder: 'newest' | 'oldest'
): ArtistBucket[] {
  const byId = new Map<string, ArtistBucket>()
  for (const chat of chats) {
    const id = chat.artistId || 'unknown'
    const name = chat.artistName || 'Unknown artist'
    let bucket = byId.get(id)
    if (!bucket) {
      bucket = {
        artistId: id,
        artistName: name,
        chats: [],
        needsReplyCount: 0,
        nextBooking: null,
      }
      byId.set(id, bucket)
    }
    bucket.chats.push(chat)
    if (getWaitingStatus(chat) === 'waiting_artist') bucket.needsReplyCount += 1
    const bd = getEffectiveBookingDate(chat, bookingAnalyses)
    if (bd) {
      const startOfToday = new Date()
      startOfToday.setHours(0, 0, 0, 0)
      if (bd >= startOfToday) {
        if (!bucket.nextBooking || bd < bucket.nextBooking) bucket.nextBooking = bd
      }
    }
  }
  const list = Array.from(byId.values())
  list.sort((a, b) => {
    const ta = bucketLatestActivityMs(a)
    const tb = bucketLatestActivityMs(b)
    if (ta !== tb) {
      return sortOrder === 'newest' ? tb - ta : ta - tb
    }
    if (b.needsReplyCount !== a.needsReplyCount) return b.needsReplyCount - a.needsReplyCount
    const an = a.nextBooking?.getTime() ?? Infinity
    const bn = b.nextBooking?.getTime() ?? Infinity
    if (an !== bn) return an - bn
    return a.artistName.localeCompare(b.artistName)
  })
  return list
}

export function ArtistCentricOpsView({
  chats,
  statusFilter,
  sortOrder,
  onDeleteChat,
  bookingAnalyses,
  analyzingChatIds,
  onAnalyzeSingleChat,
  analyticsLoadState = 'idle',
}: ArtistCentricOpsViewProps) {
  const [selectedArtistId, setSelectedArtistId] = useState<string | null>(null)
  const [selectedDay, setSelectedDay] = useState<string | null>(null)
  const [artistSearch, setArtistSearch] = useState('')
  const [avatarByArtistId, setAvatarByArtistId] = useState<Record<string, string | null>>({})

  const buckets = useMemo(
    () => buildArtistBuckets(chats, bookingAnalyses, sortOrder),
    [chats, bookingAnalyses, sortOrder]
  )

  const filteredBuckets = useMemo(() => {
    const q = artistSearch.trim().toLowerCase()
    if (!q) return buckets
    return buckets.filter(
      b => b.artistName.toLowerCase().includes(q) || b.artistId.toLowerCase().includes(q)
    )
  }, [buckets, artistSearch])

  const uniqueArtistIds = useMemo(() => [...new Set(chats.map(c => c.artistId).filter(Boolean))], [chats])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const updates: Record<string, string | null> = {}
      await Promise.all(
        uniqueArtistIds.map(async id => {
          try {
            const snap = await getDoc(doc(db, 'artists', id))
            if (!snap.exists()) {
              updates[id] = null
              return
            }
            const data = snap.data() as { compressedAvatarUrl?: string; avatarUrl?: string }
            updates[id] = data.compressedAvatarUrl || data.avatarUrl || null
          } catch {
            updates[id] = null
          }
        })
      )
      if (!cancelled) setAvatarByArtistId(prev => ({ ...prev, ...updates }))
    })()
    return () => {
      cancelled = true
    }
  }, [uniqueArtistIds.join(',')])

  const artistScopedChats = useMemo(() => {
    if (!selectedArtistId) return chats
    return chats.filter(c => c.artistId === selectedArtistId)
  }, [chats, selectedArtistId])

  const scopedChats = useMemo(() => {
    let list = artistScopedChats
    if (selectedDay) {
      list = list.filter(c => {
        const d = getEffectiveBookingDate(c, bookingAnalyses)
        return d && format(d, 'yyyy-MM-dd') === selectedDay
      })
    }
    return list
  }, [artistScopedChats, selectedDay, bookingAnalyses])

  const selectedArtistLabel =
    selectedArtistId == null
      ? 'All artists'
      : buckets.find(b => b.artistId === selectedArtistId)?.artistName ?? 'Artist'

  const selectedArtistAvatarUrl =
    selectedArtistId == null ? null : avatarByArtistId[selectedArtistId] ?? null

  const loadBusy = analyticsLoadState !== 'idle'

  return (
    <div
      className={cn(
        'relative flex min-h-0 flex-1 flex-col gap-4 max-lg:overflow-visible lg:overflow-hidden lg:gap-6',
        loadBusy && 'min-h-[min(40vh,360px)]'
      )}
      aria-busy={loadBusy}
    >
      {analyticsLoadState === 'full_refresh' && (
        <div
          className="absolute inset-0 z-30 cursor-wait rounded-xl bg-background/55 backdrop-blur-[2px] motion-safe:transition-opacity"
          aria-hidden
        />
      )}
      {analyticsLoadState === 'filter_refresh' && (
        <div
          className="absolute inset-0 z-30 cursor-wait rounded-xl bg-background/25"
          aria-hidden
        />
      )}
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col gap-4 rounded-xl border border-border bg-card/50 p-3 motion-safe:transition-[opacity,filter] max-lg:overflow-visible lg:gap-4 lg:overflow-hidden lg:p-4',
          analyticsLoadState === 'full_refresh' && 'pointer-events-none select-none opacity-45 saturate-50',
          analyticsLoadState === 'filter_refresh' && 'pointer-events-none select-none opacity-80'
        )}
      >
      {/* Mobile: artist scope */}
      <div className="flex shrink-0 flex-col gap-3 lg:hidden">
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">Artist</label>
          <select
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            value={selectedArtistId ?? ''}
            onChange={e => {
              const v = e.target.value
              setSelectedArtistId(v === '' ? null : v)
              setSelectedDay(null)
            }}
          >
            <option value="">All artists</option>
            {buckets.map(b => (
              <option key={b.artistId} value={b.artistId}>
                {b.artistName} ({b.chats.length})
                {b.needsReplyCount > 0 ? ` · ${b.needsReplyCount} need reply` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 max-lg:overflow-visible lg:grid-cols-[minmax(200px,240px)_minmax(0,1fr)_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:items-stretch lg:gap-4 lg:overflow-hidden">
        {/* Artist rail — desktop */}
        <aside className="hidden min-h-0 overflow-hidden rounded-lg border border-border bg-background lg:flex lg:flex-col">
          <div className="shrink-0 border-b border-border p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Users className="h-4 w-4 shrink-0" />
              Artists
            </div>
            <Input
              placeholder="Search…"
              value={artistSearch}
              onChange={e => setArtistSearch(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <div className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-y-contain p-2">
            <button
              type="button"
              onClick={() => {
                setSelectedArtistId(null)
                setSelectedDay(null)
              }}
              className={cn(
                'w-full rounded-lg border px-2 py-2 text-left text-sm transition-colors',
                selectedArtistId === null
                  ? 'border-primary bg-primary/10'
                  : 'border-transparent hover:bg-muted/60'
              )}
            >
              <div className="font-medium">All artists</div>
              <div className="text-xs text-muted-foreground">{chats.length} chats</div>
            </button>
            {filteredBuckets.map(b => {
              const selected = selectedArtistId === b.artistId
              const url = avatarByArtistId[b.artistId]
              return (
                <button
                  key={b.artistId}
                  type="button"
                  onClick={() => {
                    setSelectedArtistId(b.artistId)
                    setSelectedDay(null)
                  }}
                  className={cn(
                    'w-full rounded-lg border px-2 py-2 text-left text-sm transition-colors flex gap-2 items-start',
                    selected ? 'border-primary bg-primary/10' : 'border-transparent hover:bg-muted/60'
                  )}
                >
                  {url ? (
                    <img
                      src={url}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-full object-cover bg-muted"
                    />
                  ) : (
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                      {artistInitials(b.artistName)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{b.artistName}</div>
                    <div className="text-xs text-muted-foreground">
                      {b.chats.length} chat{b.chats.length === 1 ? '' : 's'}
                      {b.needsReplyCount > 0 && (
                        <span className="text-amber-600 dark:text-amber-400">
                          {' '}
                          · {b.needsReplyCount} need reply
                        </span>
                      )}
                    </div>
                    {b.nextBooking && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Next: {format(b.nextBooking, 'd MMM')}
                      </div>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        </aside>

        {/* Chat list — same row height as calendar on lg+; scrolls inside */}
        <section className="flex min-h-0 min-w-0 flex-col overflow-hidden max-lg:max-h-[min(60vh,560px)] lg:min-h-0">
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain [scrollbar-gutter:stable]">
            <ChatGrid
              chats={scopedChats}
              statusFilter={statusFilter}
              dateFilter="all"
              sortOrder={sortOrder}
              onDeleteChat={onDeleteChat}
              bookingAnalyses={bookingAnalyses}
              allChatsForUserLookup={chats}
              onNavigateToUserChat={({ chatId, artistId }) => {
                setSelectedArtistId(artistId)
                setSelectedDay(null)
                window.requestAnimationFrame(() => {
                  window.requestAnimationFrame(() => {
                    document
                      .getElementById(`chat-grid-row-${chatId}`)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                  })
                })
              }}
            />
          </div>
        </section>

        {/* Bookings calendar — 50% of main row on lg+; full width below chat on mobile */}
        <aside className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-background p-3 max-lg:max-h-[min(55vh,520px)] lg:min-h-0">
          <div className="mb-2 flex shrink-0 items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold leading-tight">Bookings calendar</div>
              <p className="mt-0.5 text-xs text-muted-foreground leading-snug">
                {selectedArtistId == null ? 'All bookings' : `${selectedArtistLabel} only`}
              </p>
            </div>
            {selectedArtistId != null && (
              <div className="shrink-0 pt-0.5">
                {selectedArtistAvatarUrl ? (
                  <img
                    src={selectedArtistAvatarUrl}
                    alt=""
                    className="h-10 w-10 rounded-full border border-border/60 bg-muted object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border/60 bg-muted text-xs font-semibold">
                    {artistInitials(selectedArtistLabel)}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <BookingsCalendarView
              chats={artistScopedChats}
              onDeleteChat={onDeleteChat}
              bookingAnalyses={bookingAnalyses}
              analyzingChatIds={analyzingChatIds}
              onAnalyzeSingleChat={onAnalyzeSingleChat}
              layout="calendarOnly"
              selectedDateKey={selectedDay}
              onSelectedDateKeyChange={setSelectedDay}
            />
          </div>
        </aside>
      </div>
      </div>
    </div>
  )
}
